import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import { IDIOMAS } from '../../i18n/idiomas';
import { cambiarIdioma } from '../../i18n';
import '../../css/selector-idioma.css';

/**
 * Selector de idioma de la interfaz.
 *
 * @param variante  'menu' (desplegable compacto, cabeceras) o 'campo'
 *                  (opciones en línea, para formularios como el perfil)
 * @param onCambio  se llama con el nuevo código tras aplicarlo. El perfil lo
 *                  usa para guardar la preferencia en la cuenta.
 */
const SelectorIdioma = ({ variante = 'menu', value, onCambio }) => {
  const { t, i18n } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  const contenedorRef = useRef(null);

  // En el formulario manda el valor controlado; en la cabecera, el idioma vivo
  const actual = value || i18n.language;
  const idiomaActual = IDIOMAS.find((idioma) => idioma.codigo === actual) || IDIOMAS[0];

  useEffect(() => {
    if (!abierto) return undefined;
    const alPulsarFuera = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) setAbierto(false);
    };
    const alPulsarEscape = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', alPulsarFuera);
    document.addEventListener('keydown', alPulsarEscape);
    return () => {
      document.removeEventListener('mousedown', alPulsarFuera);
      document.removeEventListener('keydown', alPulsarEscape);
    };
  }, [abierto]);

  const seleccionar = (codigo) => {
    cambiarIdioma(codigo);
    setAbierto(false);
    if (onCambio) onCambio(codigo);
  };

  if (variante === 'campo') {
    return (
      <div className="selector-idioma-campo" role="radiogroup" aria-label={t('idioma.seleccionar')}>
        {IDIOMAS.map((idioma) => {
          const activo = idioma.codigo === actual;
          return (
            <button
              key={idioma.codigo}
              type="button"
              role="radio"
              aria-checked={activo}
              className={`selector-idioma-opcion ${activo ? 'activo' : ''}`}
              onClick={() => seleccionar(idioma.codigo)}
            >
              <img className="selector-idioma-bandera" src={idioma.bandera} alt="" aria-hidden="true" />
              <span className="selector-idioma-nombre">{idioma.nombre}</span>
              {activo && <FiCheck className="selector-idioma-check" />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="selector-idioma" ref={contenedorRef}>
      <button
        type="button"
        className="selector-idioma-trigger"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="listbox"
        aria-label={t('idioma.actual', { idioma: idiomaActual.nombre })}
        title={t('idioma.seleccionar')}
      >
        <img className="selector-idioma-bandera" src={idiomaActual.bandera} alt="" aria-hidden="true" />
        <span className="selector-idioma-corto">{idiomaActual.corto}</span>
        <FiChevronDown className="selector-idioma-chevron" />
      </button>

      {abierto && (
        <ul className="selector-idioma-lista" role="listbox" aria-label={t('idioma.seleccionar')}>
          {IDIOMAS.map((idioma) => {
            const activo = idioma.codigo === actual;
            return (
              <li key={idioma.codigo}>
                <button
                  type="button"
                  role="option"
                  aria-selected={activo}
                  className={`selector-idioma-item ${activo ? 'activo' : ''}`}
                  onClick={() => seleccionar(idioma.codigo)}
                >
                  <img className="selector-idioma-bandera" src={idioma.bandera} alt="" aria-hidden="true" />
                  {idioma.nombre}
                  {activo && <FiCheck className="selector-idioma-check" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SelectorIdioma;
